import React, { useCallback, useEffect, useRef, useState } from 'react';
import Quill from 'quill';

/**
 * Alças de redimensionamento/rotação para as imagens da caixa de ação, no estilo
 * do Google Docs: ao clicar na imagem aparecem os pontos nas bordas; arrastar um
 * canto redimensiona mantendo a proporção, as laterais esticam livremente e a
 * alça de cima gira. Um mini-menu escolhe se a imagem fica "em linha" com o
 * texto ou "abaixo" dele (item 4b).
 *
 * O tamanho/rotação são gravados no atributo style da própria <img>, que é
 * preservado pelo blot estendido em utils/quillImageAttributes.
 */

const MIN_SIZE = 40;

// Cantos mantêm proporção; laterais esticam só um eixo.
const HANDLES = [
    { key: 'nw', x: 0, y: 0, corner: true, cursor: 'nwse-resize' },
    { key: 'n', x: 0.5, y: 0, corner: false, axis: 'y', cursor: 'ns-resize' },
    { key: 'ne', x: 1, y: 0, corner: true, cursor: 'nesw-resize' },
    { key: 'e', x: 1, y: 0.5, corner: false, axis: 'x', cursor: 'ew-resize' },
    { key: 'se', x: 1, y: 1, corner: true, cursor: 'nwse-resize' },
    { key: 's', x: 0.5, y: 1, corner: false, axis: 'y', cursor: 'ns-resize' },
    { key: 'sw', x: 0, y: 1, corner: true, cursor: 'nesw-resize' },
    { key: 'w', x: 0, y: 0.5, corner: false, axis: 'x', cursor: 'ew-resize' },
];

const readRotation = (img) => {
    const match = /rotate\((-?[\d.]+)deg\)/i.exec(img.style.transform || '');
    return match ? parseFloat(match[1]) : 0;
};

const isInline = (img) => (img.style.display || '').indexOf('inline') === 0;

const QuillImageResizer = ({ quillRef, onCommit, disabled = false }) => {
    const [img, setImg] = useState(null);
    // box em coordenadas de viewport: centro + dimensões sem rotação + ângulo
    const [box, setBox] = useState(null);
    const dragRef = useRef(null);
    const imgRef = useRef(null);

    imgRef.current = img;

    const getEditorRoot = useCallback(() => {
        const quill = quillRef?.current?.getEditor?.();
        return quill ? quill.root : null;
    }, [quillRef]);

    const measure = useCallback(() => {
        const el = imgRef.current;
        if (!el || !el.isConnected) {
            setImg(null);
            setBox(null);
            return;
        }
        const rect = el.getBoundingClientRect();
        setBox({
            cx: rect.left + rect.width / 2,
            cy: rect.top + rect.height / 2,
            w: el.offsetWidth,
            h: el.offsetHeight,
            angle: readRotation(el),
            top: rect.top,
        });
    }, []);

    // Seleciona a imagem clicada dentro do editor.
    useEffect(() => {
        if (disabled) return undefined;
        const root = getEditorRoot();
        if (!root) return undefined;

        const onClick = (e) => {
            if (e.target && e.target.tagName === 'IMG') {
                setImg(e.target);
            } else {
                setImg(null);
                setBox(null);
            }
        };
        root.addEventListener('click', onClick);
        return () => root.removeEventListener('click', onClick);
    }, [getEditorRoot, disabled]);

    // Recalcula a posição sempre que a seleção muda, a janela rola ou redimensiona.
    useEffect(() => {
        if (!img) return undefined;
        measure();
        let frame = null;
        const schedule = () => {
            if (frame) return;
            frame = requestAnimationFrame(() => {
                frame = null;
                measure();
            });
        };
        window.addEventListener('scroll', schedule, true);
        window.addEventListener('resize', schedule);
        return () => {
            if (frame) cancelAnimationFrame(frame);
            window.removeEventListener('scroll', schedule, true);
            window.removeEventListener('resize', schedule);
        };
    }, [img, measure]);

    // Clique fora / Esc desmarca.
    useEffect(() => {
        if (!img) return undefined;
        const onDocMouseDown = (e) => {
            if (e.target.closest?.('[data-quill-resizer]')) return;
            if (e.target === img) return;
            setImg(null);
            setBox(null);
        };
        const onKey = (e) => {
            if (e.key === 'Escape') {
                setImg(null);
                setBox(null);
            }
        };
        document.addEventListener('mousedown', onDocMouseDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocMouseDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [img]);

    // Grava as alterações. Além de mutar o DOM (feito ao arrastar), aplica o
    // style pela API do Quill (formatText) para que o próprio delta carregue o
    // atributo: sem isto o Quill poderia descartar o style ao re-serializar o
    // valor controlado, e o ajuste se perderia ao salvar (item 4b).
    const commit = useCallback(() => {
        const quill = quillRef?.current?.getEditor?.();
        if (!quill) return;
        const el = imgRef.current;
        if (el) {
            try {
                const blot = Quill.find(el);
                if (blot) {
                    const index = quill.getIndex(blot);
                    const css = el.getAttribute('style') || '';
                    quill.formatText(index, 1, 'style', css, 'user');
                }
            } catch (err) {
                /* fallback: o innerHTML abaixo ainda reflete a mutação no DOM */
            }
        }
        try {
            quill.update();
        } catch (err) {
            /* segue */
        }
        if (onCommit) onCommit(quill.root.innerHTML);
    }, [quillRef, onCommit]);

    const startResize = (e, handle) => {
        e.preventDefault();
        e.stopPropagation();
        if (!img) return;
        dragRef.current = {
            mode: 'resize',
            handle,
            startX: e.clientX,
            startY: e.clientY,
            startW: img.offsetWidth,
            startH: img.offsetHeight,
            ratio: img.offsetWidth / (img.offsetHeight || 1),
        };
    };

    const startRotate = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!img || !box) return;
        dragRef.current = {
            mode: 'rotate',
            cx: box.cx,
            cy: box.cy,
            startAngle: readRotation(img),
            startPointer: Math.atan2(e.clientY - box.cy, e.clientX - box.cx),
        };
    };

    useEffect(() => {
        const onMove = (e) => {
            const drag = dragRef.current;
            const el = imgRef.current;
            if (!drag || !el) return;

            if (drag.mode === 'rotate') {
                const pointer = Math.atan2(e.clientY - drag.cy, e.clientX - drag.cx);
                let deg = drag.startAngle + ((pointer - drag.startPointer) * 180) / Math.PI;
                if (e.shiftKey) deg = Math.round(deg / 15) * 15;
                deg = Math.round(deg * 10) / 10;
                el.style.transform = deg ? `rotate(${deg}deg)` : '';
                measure();
                return;
            }

            const dx = e.clientX - drag.startX;
            const dy = e.clientY - drag.startY;
            const { handle } = drag;
            let w = drag.startW;
            let h = drag.startH;

            if (handle.corner) {
                // Sinal conforme o canto: arrastar para fora aumenta.
                const signX = handle.x === 1 ? 1 : -1;
                const signY = handle.y === 1 ? 1 : -1;
                // Usa o maior deslocamento para manter a proporção estável.
                const byX = drag.startW + dx * signX;
                const byY = (drag.startH + dy * signY) * drag.ratio;
                w = Math.abs(dx * signX) >= Math.abs(dy * signY) ? byX : byY;
                h = w / drag.ratio;
            } else if (handle.axis === 'x') {
                const signX = handle.x === 1 ? 1 : -1;
                w = drag.startW + dx * signX;
                h = drag.startH;
            } else {
                const signY = handle.y === 1 ? 1 : -1;
                h = drag.startH + dy * signY;
                w = drag.startW;
            }

            w = Math.max(MIN_SIZE, Math.round(w));
            h = Math.max(MIN_SIZE, Math.round(h));

            el.style.width = `${w}px`;
            el.style.height = handle.corner ? 'auto' : `${h}px`;
            if (handle.corner) el.style.height = 'auto';
            measure();
        };

        const onUp = () => {
            if (!dragRef.current) return;
            dragRef.current = null;
            commit();
            measure();
        };

        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
        return () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
    }, [commit, measure]);

    const applyFlow = (inline) => {
        if (!img) return;
        if (inline) {
            img.style.display = 'inline-block';
            img.style.verticalAlign = 'middle';
            img.style.margin = '0 4px';
        } else {
            img.style.display = 'block';
            img.style.verticalAlign = '';
            img.style.margin = '6px 0';
        }
        commit();
        measure();
    };

    const resetSize = () => {
        if (!img) return;
        img.style.width = '';
        img.style.height = '';
        img.style.transform = '';
        commit();
        measure();
    };

    if (disabled || !img || !box) return null;

    const inline = isInline(img);
    const handleStyle = {
        position: 'absolute',
        width: 10,
        height: 10,
        marginLeft: -5,
        marginTop: -5,
        borderRadius: 2,
        background: '#fff',
        border: '1.5px solid #1a73e8',
        boxSizing: 'border-box',
        pointerEvents: 'auto',
    };

    return (
        <>
            {/* Moldura + alças: acompanha a rotação da imagem */}
            <div
                data-quill-resizer
                style={{
                    position: 'fixed',
                    left: box.cx - box.w / 2,
                    top: box.cy - box.h / 2,
                    width: box.w,
                    height: box.h,
                    transform: box.angle ? `rotate(${box.angle}deg)` : undefined,
                    border: '1.5px solid #1a73e8',
                    boxSizing: 'border-box',
                    pointerEvents: 'none',
                    zIndex: 1300,
                }}
            >
                {HANDLES.map((h) => (
                    <div
                        key={h.key}
                        role="presentation"
                        onMouseDown={(e) => startResize(e, h)}
                        style={{
                            ...handleStyle,
                            left: `${h.x * 100}%`,
                            top: `${h.y * 100}%`,
                            cursor: h.cursor,
                        }}
                    />
                ))}
                {/* Alça de rotação, acima da borda superior */}
                <div
                    role="presentation"
                    onMouseDown={startRotate}
                    title="Girar (segure Shift para 15º)"
                    style={{
                        ...handleStyle,
                        left: '50%',
                        top: -22,
                        borderRadius: '50%',
                        cursor: 'grab',
                    }}
                />
            </div>

            {/* Mini-menu: em linha x abaixo, e tamanho original */}
            <div
                data-quill-resizer
                style={{
                    position: 'fixed',
                    left: box.cx,
                    top: box.top - 44,
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    gap: 4,
                    padding: 4,
                    borderRadius: 6,
                    background: '#fff',
                    boxShadow: '0 2px 8px rgba(0,0,0,.25)',
                    zIndex: 1301,
                }}
            >
                {[
                    { label: 'Em linha', active: inline, onClick: () => applyFlow(true) },
                    { label: 'Abaixo', active: !inline, onClick: () => applyFlow(false) },
                    { label: 'Tamanho original', active: false, onClick: resetSize },
                ].map((btn) => (
                    <button
                        key={btn.label}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={btn.onClick}
                        style={{
                            border: 'none',
                            borderRadius: 4,
                            padding: '4px 8px',
                            fontSize: 12,
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            background: btn.active ? '#e8f0fe' : 'transparent',
                            color: btn.active ? '#1a73e8' : '#3c4043',
                            fontWeight: btn.active ? 600 : 400,
                        }}
                    >
                        {btn.label}
                    </button>
                ))}
            </div>
        </>
    );
};

export default QuillImageResizer;
